import { useEffect, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { formatPrice } from "@/utils/format-price";

interface FinanziamentoCheckProps {
    prezzoAuto: number;
    km: number;
    tassoAgevolato?: boolean;
    onChange: (data: {
        anticipo: number;
        durata: number;
        rata: number;
        tasso: number;
        importo: number;
    }) => void;
}

const SPESA_ISTRUTTORIA = 350;
const IMPOSTA_BOLLO = 16;

const getMinDurata = (importo: number): number => {
    if (importo < 7500) return 3; // 6000–7499 € → minimo 36 mesi (3 anni)
    return 2;                      // ≥ 7500 € → minimo 24 mesi (2 anni)
};

const FinanziamentoCheck = ({
    prezzoAuto,
    km,
    tassoAgevolato = false,
    onChange,
}: FinanziamentoCheckProps) => {
    const [anticipo, setAnticipo] = useState(0);
    const [durata, setDurata] = useState(8);
    const [rata, setRata] = useState(0);

    const maxAnticipo = prezzoAuto - 6000;
    const tasso = (km ?? 0) <= 5000
        ? (tassoAgevolato ? 6.95 : 7.95)
        : (tassoAgevolato ? 7.95 : 8.95);

    const minDurata = getMinDurata(prezzoAuto - anticipo);
    const effectiveDurata = Math.max(durata, minDurata);

    useEffect(() => {
        const prezzoFinanziato = (prezzoAuto - anticipo) + SPESA_ISTRUTTORIA + IMPOSTA_BOLLO;
        const interessi = tasso / 100 / 12;
        const mensilita = effectiveDurata * 12;
        const costante = Math.pow(1 + interessi, mensilita);
        const rataMensile = (prezzoFinanziato * costante * interessi) / (costante - 1);

        setRata(rataMensile);

        if (durata !== effectiveDurata) {
            setDurata(effectiveDurata);
        }

        onChange({
            anticipo,
            durata: mensilita,
            rata: rataMensile,
            tasso,
            importo: prezzoAuto - anticipo,
        });
    }, [prezzoAuto, anticipo, durata, tasso]);

    return (
        <div className="grid w-full gap-6 md:grid-cols-[2fr_1fr]">
            <div className="space-y-4">

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">Anticipo</p>
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                            {formatPrice(anticipo)}
                        </span>
                    </div>
                    <div className="mt-3">
                        <Slider
                            value={[anticipo]}
                            min={0}
                            max={maxAnticipo}
                            step={100}
                            onValueChange={(v) => setAnticipo(v[0])}
                        />
                        <div className="mt-2 flex justify-between text-xs text-gray-400">
                            <span>0 €</span>
                            <span>{formatPrice(maxAnticipo)}</span>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">Durata</p>
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
                            {effectiveDurata} {effectiveDurata === 1 ? "anno" : "anni"}
                        </span>
                    </div>
                    <div className="mt-3">
                        <Slider
                            value={[effectiveDurata]}
                            min={minDurata}
                            max={8}
                            step={1}
                            onValueChange={(v) => setDurata(v[0])}
                        />
                        <div className="mt-2 flex justify-between text-xs text-gray-400">
                            <span>{minDurata} {minDurata === 1 ? "anno" : "anni"}</span>
                            <span>8 anni</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex h-full flex-col justify-center rounded-2xl border border-primary/30 bg-primary/5 p-5">
                <p className="text-sm font-medium text-primary">Rata mensile stimata</p>
                <div className="mt-3 flex items-end gap-2">
                    <p className="text-4xl font-bold text-gray-900">{formatPrice(rata)}</p>
                    <span className="text-base text-gray-500">/mese</span>
                </div>
                <p className="mt-3 text-xs text-gray-500">
                    Importo indicativo, calcolato con TAN {tasso}%.
                </p>
                <p className="mt-2 text-[11px] leading-relaxed text-gray-400">
                    Salvo approvazione della Finanziaria. Fogli informativi disponibili presso la Concessionaria.
                    Offerta valida salvo esaurimento scorte.
                </p>
            </div>
        </div>
    );
};

export default FinanziamentoCheck;
